import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../../api';
import AccountSelector from '../../components/AccountSelector';

// ============================================================
// Tagging Request form (Aug 13 revamp — Option B route-first scheduling)
//
// One step, no calendar. Every submission lands in the Tagging Manager's
// Awaiting Scheduling queue; the Build Day optimizer plans efficient
// routes by real location, and the submitter gets a text the moment a
// work date is assigned. Emergency requests jump the queue and page the
// Tagging Manager immediately.
// ============================================================

function fmtDuration(mins) {
  if (!mins) return '';
  if (mins < 60) return `${mins} minutes`;
  const h = mins / 60;
  return h === 1 ? '1 hour' : `${h} hours`;
}

const label = { fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 };
const req = <span style={{ color: '#ef4444' }}>*</span>;

export default function TaggingRequestFormPage() {
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // confirmation screen

  const [property, setProperty] = useState(null);
  const [workTypeIds, setWorkTypeIds] = useState([]);   // exactly one — Light Tag or Full Tag
  const [frequency, setFrequency] = useState('single');
  const [workDescription, setWorkDescription] = useState('');
  const [estimate, setEstimate] = useState(null);   // computed server-side from unit count
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [specialTaggingDescription, setSpecialTaggingDescription] = useState('');
  const [accessInstructions, setAccessInstructions] = useState('');
  const [photos, setPhotos] = useState([]);
  const [isEmergency, setIsEmergency] = useState(false);

  // Arriving from a New Account Setup step. Carrying the id through means the
  // server links THIS tagging request back to that launch instead of the setup
  // spawning a second copy of the same job.
  const [searchParams] = useSearchParams();
  const setupId = searchParams.get('setup_id');
  const [setupPrefill, setSetupPrefill] = useState(null);

  useEffect(() => {
    api.getTaggingRequestConfig().then(setConfig).catch(e => setError(e.message));
  }, []);

  // Prefill the property from the setup so the rep isn't retyping what they
  // just entered two steps ago.
  useEffect(() => {
    if (!setupId) return;
    api.getNewAccountSetupPrefill(setupId, 'tagging_request')
      .then(p => {
        setSetupPrefill(p);
        if (p.property_id) {
          setProperty({ id: p.property_id, name: p.property_name, address: p.address, city: p.city, region: p.region });
        }
      })
      .catch(() => { /* prefill is a convenience, not a gate */ });
  }, [setupId]);

  // The estimate is computed from the property's unit count, not asked for.
  // Fetched on property change so the rep sees the number and its basis.
  useEffect(() => {
    if (!property?.id) { setEstimate(null); return; }
    let cancelled = false;
    setEstimateLoading(true);
    api.getTaggingEstimate(property.id)
      .then(e => { if (!cancelled) setEstimate(e); })
      .catch(() => { if (!cancelled) setEstimate(null); })
      .finally(() => { if (!cancelled) setEstimateLoading(false); });
    return () => { cancelled = true; };
  }, [property?.id]);

  const selectedTypes = config?.work_types?.filter(wt => workTypeIds.includes(wt.id)) || [];
  const needsSpecialDesc = selectedTypes.some(wt => wt.requires_description);
  const needsPhotos = selectedTypes.filter(wt => wt.requires_photos);

  // One tag type per job — a Full Tag already contains everything a Light Tag
  // covers, so this is a choice, not a checklist.
  function pickTagType(id) {
    setWorkTypeIds([id]);
  }

  function validate() {
    if (!property?.id) return 'Property is required';
    if (workTypeIds.length === 0) return 'Choose the type of tag: Light Tag or Full Tag';
    if (!workDescription.trim()) return 'Work description is required';
    if (needsSpecialDesc && !specialTaggingDescription.trim()) return 'Describe the special tagging work';
    if (needsPhotos.length > 0 && photos.length === 0) {
      return `Photos are required for: ${needsPhotos.map(wt => wt.name).join(', ')}`;
    }
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);
    setError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('property_id', property.id);
      fd.append('work_type_ids', JSON.stringify(workTypeIds));
      fd.append('frequency', frequency);
      fd.append('work_description', workDescription.trim());
      if (needsSpecialDesc) fd.append('special_tagging_description', specialTaggingDescription.trim());
      if (accessInstructions.trim()) fd.append('access_instructions', accessInstructions.trim());
      if (isEmergency) fd.append('is_emergency', '1');
      // Links this record back to the launch that sent us here (server-side
      // seam in taggingRequests.js). Without it the setup's step stays empty
      // and someone eventually files a duplicate.
      if (setupId) fd.append('setup_id', setupId);
      for (const f of photos) fd.append('photos', f);

      const result = await api.submitTaggingRequest(fd);
      setCreated(result);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── confirmation screen ──
  if (created) {
    return (
      <div className="admin-page" style={{ maxWidth: 640 }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>{created.is_emergency ? '🚨' : '✅'}</div>
          <h2 style={{ margin: '0 0 6px' }}>Tagging Request #{created.id} submitted</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
            {created.is_emergency
              ? 'Emergency — the Tagging Manager has been notified and will prioritize it.'
              : 'In the scheduling queue. You’ll get a text as soon as a work date is assigned.'}
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12, margin: '20px 0', textAlign: 'left',
          }}>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Property</div>
              <div style={{ fontWeight: 600 }}>{created.account_name || '—'}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Work</div>
              <div style={{ fontWeight: 600 }}>{created.tagging_work_summary}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Estimated</div>
              <div style={{ fontWeight: 600 }}>{fmtDuration(created.estimated_minutes)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Link to={`/sales/requests/tagging_request/${created.id}`} className="btn btn-primary">View Request</Link>
            <Link to="/sales/requests/tagging_request" className="btn btn-muted">Back to Tagging Requests</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div className="admin-page"><p>{error || 'Loading…'}</p></div>;
  }

  return (
    <div className="admin-page" style={{ maxWidth: 720 }}>
      <div className="page-header">
        <h2>🏷️ New Tagging Request</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Describe the work — the Tagging Manager schedules the most efficient route and you&rsquo;ll be texted the work date.
        </p>
      </div>

      {error && <p className="alert alert-error">{error}</p>}

      <form onSubmit={handleSubmit} className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <AccountSelector
          value={property}
          onChange={setProperty}
          useOther={false}
          onToggleOther={() => {}}
          otherValue=""
          onOtherChange={() => {}}
          label="Property"
          required
        />

        {/* Work address — always the property's own address. Tagging happens
            where the vehicles are, so there is nothing to override. */}
        {property && (
          <div>
            <label style={label}>Work Address</label>
            <div style={{ fontSize: 14, color: '#334155' }}>
              {[property.address, property.city].filter(Boolean).join(', ') || 'No address on file'}
            </div>
          </div>
        )}

        {/* Tag Type — one choice. The violation list under each option comes
            from the work-type catalog, so Matt rewords it in Tagging Setup. */}
        <div>
          <label style={label}>What Kind of Tag? {req}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
            {config.work_types.map(wt => {
              const checked = workTypeIds[0] === wt.id;
              return (
                <label key={wt.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                  border: `2px solid ${checked ? '#b45309' : 'var(--border-color, #e2e8f0)'}`,
                  borderRadius: 8, cursor: 'pointer',
                  background: checked ? 'rgba(180, 83, 9, 0.06)' : 'transparent',
                }}>
                  <input type="radio" name="tag_type" checked={checked}
                    onChange={() => pickTagType(wt.id)} style={{ marginTop: 3 }} />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: checked ? 700 : 600 }}>
                      {wt.name}
                      {wt.requires_photos ? <span title="Photos required" style={{ fontSize: 12, marginLeft: 6 }}>📷</span> : null}
                    </span>
                    {wt.description ? (
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.45 }}>
                        {wt.description}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Tagging Frequency — a standing sweep is set up here, once. */}
        <div>
          <label style={label}>Tagging Frequency {req}</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(config.frequencies || [{ key: 'single', label: 'Single Request' }]).map(f => {
              const checked = frequency === f.key;
              return (
                <label key={f.key} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                  border: `2px solid ${checked ? '#b45309' : 'var(--border-color, #e2e8f0)'}`,
                  borderRadius: 8, cursor: 'pointer', fontSize: 14,
                  fontWeight: checked ? 700 : 400,
                  background: checked ? 'rgba(180, 83, 9, 0.06)' : 'transparent',
                }}>
                  <input type="radio" name="frequency" checked={checked}
                    onChange={() => setFrequency(f.key)} />
                  <span>{f.key !== 'single' ? '🔁 ' : ''}{f.label}</span>
                </label>
              );
            })}
          </div>
          {frequency !== 'single' && (
            <div style={{
              fontSize: 12, color: '#92400e', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginTop: 8,
            }}>
              This becomes a standing sweep. Each time the tagger completes a visit, the next
              one is created automatically and dated {frequency === 'monthly' ? 'a month' : 'three months'} out.
              It waits in <strong>Upcoming Recurring</strong> on the schedule board until it's due.
            </div>
          )}
        </div>

        {/* Special tagging description — only when a type requires it */}
        {needsSpecialDesc && (
          <div>
            <label style={label}>Special Tagging Instructions {req}</label>
            <textarea className="form-input" rows={3}
              value={specialTaggingDescription} onChange={e => setSpecialTaggingDescription(e.target.value)}
              placeholder="Anything the tagger needs to know before they go out?"
              style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
          </div>
        )}

        {/* Work description */}
        <div>
          <label style={label}>Work Description {req}</label>
          <textarea className="form-input" rows={4}
            value={workDescription} onChange={e => setWorkDescription(e.target.value)}
            placeholder="What needs to be done, where on the property, anything the tech should know"
            style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
        </div>

        {/* Time — computed, not asked. Reps can't estimate this and shouldn't
            have to; a guess would silently drive the day plan. */}
        {property && (
          <div>
            <label style={label}>Time Needed</label>
            {estimateLoading ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Working it out…</div>
            ) : estimate ? (
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                padding: '10px 14px', borderRadius: 8,
                background: 'var(--bg-inset, #f1f5f9)', border: '1px solid var(--border-color, #e2e8f0)',
              }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                  {fmtDuration(estimate.minutes)}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {estimate.basis === 'units'
                    ? `based on ${estimate.units.toLocaleString('en-US')} units`
                    : 'standard estimate — no unit count on this property yet'}
                </span>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Pick a property first.</div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Worked out from the property size — the Tagging Manager can adjust it when planning the day.
            </div>
          </div>
        )}

        {/* Photos — optional unless a selected work type requires them */}
        <div>
          <label style={label}>
            Photos {needsPhotos.length > 0
              ? <span style={{ color: '#b91c1c' }}>* required for {needsPhotos.map(wt => wt.name).join(', ')}</span>
              : <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional, up to 10)</span>}
          </label>
          <input type="file" accept="image/*" multiple
            onChange={e => setPhotos(Array.from(e.target.files || []).slice(0, 10))} />
          {photos.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {photos.length} photo{photos.length === 1 ? '' : 's'} attached
            </div>
          )}
        </div>

        {/* Access instructions — optional */}
        <div>
          <label style={label}>Access Instructions <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
          <textarea className="form-input" rows={2}
            value={accessInstructions} onChange={e => setAccessInstructions(e.target.value)}
            placeholder="Gate code, lockbox, who to contact on site…"
            style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
        </div>

        {/* Emergency option */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14,
          border: `2px solid ${isEmergency ? '#dc2626' : 'var(--border-color, #e2e8f0)'}`,
          borderRadius: 10, cursor: 'pointer',
          background: isEmergency ? 'rgba(220, 38, 38, 0.05)' : 'transparent',
        }}>
          <input type="checkbox" checked={isEmergency} style={{ marginTop: 3 }}
            onChange={e => setIsEmergency(e.target.checked)} />
          <span>
            <span style={{ fontWeight: 700, color: '#dc2626' }}>🚨 Emergency Request</span>
            <span style={{ display: 'block', fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
              Pages the Tagging Manager immediately and jumps the scheduling queue.
            </span>
          </span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          <button type="button" className="btn btn-muted" disabled={submitting}
            onClick={() => navigate('/sales/requests/tagging_request')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-gold" style={{ fontWeight: 700 }} disabled={submitting}>
            {submitting ? 'Submitting…' : isEmergency ? 'Submit Emergency Request' : 'Submit Tagging Request'}
          </button>
        </div>
      </form>
    </div>
  );
}
